import { useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  capitalizeFirstLetter,
  TransactionStatus as TransactionStatusType,
  TransactionType,
  withStyles,
  formattedCurrency,
} from 'vopay';
import { format, subYears } from 'date-fns';
import { useTranslation } from 'react-i18next';
import useTransactionApproval from './useTransactionApproval';
import { useToast } from '@/providers/Toast.provider';
import { useClientData } from '@/providers/ClientData.provider';
import { ExpandableText, TransactionStatus, Title, DetailsRows } from 'UI';
import useFetchWorkflows from '@/hooks/useApi/useFetchWorkflows';
import useFetchTransactionsHistory from '@/hooks/useApi/useFetchTransactionsHistory';
import { usePermissions } from '@/providers/Permissions.provider';
import styles from './ApprovalHistoryDetailsCard.module.scss';

const cn = withStyles(styles);

type ApprovalHistoryDetailsCardProps = {
  transactionID: string;
  status: TransactionStatusType;
  type: TransactionType;
  jwt?: string;
};

type ApprovalHistoryItemType = {
  Username: string;
  ApprovalDecision: 'Approved' | 'Rejected';
  DecisionDate: string;
  Reason: string | null;
};

export default function ApprovalHistoryDetailsCard({
  transactionID,
  status,
  type,
  jwt,
}: ApprovalHistoryDetailsCardProps) {
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.ApprovalHistoryDetailsCard',
  });

  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const toast = useToast();

  const { approvalHistory, fetchApprovalHistory } = useTransactionApproval({ bearer: jwt });
  const { workflows, fetchWorkflows } = useFetchWorkflows({ bearer: jwt });

  const { fetchTransactionsHistory, transactions } = useFetchTransactionsHistory();
  const { permissions } = usePermissions();

  useEffect(() => {
    if (transactionID) {
      fetchTransactionsHistory({
        StartDateTime: format(subYears(new Date(), 2), 'yyyy-MM-dd'),
        EndDateTime: format(new Date(), 'yyyy-MM-dd'),
        TransactionID: transactionID,
        IncludeSubaccountTransactions: Boolean(permissions.ViewSubaccounts),
      });
    }
  }, [transactionID, permissions.ViewSubaccounts]);

  const isPendingApproval = status === 'pending approval';

  const {
    response: { TransactionApprovalWorkflowEnabled },
  } = useClientData();

  const isApprovalWorkflowEnabled = TransactionApprovalWorkflowEnabled !== '0';

  const amount = useMemo(() => {
    return Number(transactions[0]?.CreditAmount) || Number(transactions[0]?.DebitAmount);
  }, [transactions]);

  const currency = useMemo(() => {
    return transactions[0]?.Currency;
  }, [transactions]);

  const getApprovalWorkflows = async () => {
    try {
      await fetchWorkflows();
    } catch (err) {
      console.error(err);
      toast.neutral(tGlobal('errorOccurred'));
    }
  };

  useEffect(() => {
    getApprovalWorkflows();
  }, []);

  const isApprovalSupportedForType = useMemo(() => {
    return workflows.some((group) => group.TransactionType === type);
  }, [workflows, type]);

  const matchingTier = useMemo(() => {
    if (!amount || !workflows.length) return null;

    const group = workflows.find((g) => g.TransactionType === type);
    if (!group) return null;

    const tierIndex = group.Workflows.findIndex((wf) => {
      const min = parseFloat(wf.MinAmount);
      const max = wf.MaxAmount !== null ? parseFloat(wf.MaxAmount) : null;

      return amount >= min && (max === null || amount < max);
    });

    if (tierIndex === -1) return null;

    return { tierNumber: tierIndex + 1, workflow: group.Workflows[tierIndex] };
  }, [workflows, type, amount]);

  useEffect(() => {
    if (!transactionID || !isApprovalWorkflowEnabled || !isApprovalSupportedForType) {
      return;
    }

    (async () => {
      try {
        await fetchApprovalHistory({
          TransactionID: transactionID,
        });
      } catch (err) {
        console.error(err);
        toast.neutral(t('fetchingError', { defaultValue: 'Failed to fetch approval history' }));
      }
    })();
  }, [transactionID, TransactionApprovalWorkflowEnabled, isApprovalSupportedForType]);

  const approvalHistoryDetails = useMemo(() => {
    const history = approvalHistory ?? [];

    if (isPendingApproval) {
      const required = matchingTier?.workflow.NumberOfApprovalsRequired ?? 0;

      const normalizedApprovalHistory = history
        .slice(0, required)
        .concat(Array(Math.max(0, required - history.length)).fill(null));

      return normalizedApprovalHistory.map((item) => {
        if (!item) {
          return [
            {
              label: t('approver', { defaultValue: 'Approver' }),
              value: '-',
            },
            {
              label: t('decision', { defaultValue: 'Decision' }),
              value: '-',
            },
          ];
        }

        return mapApprovalHistoryItem(item);
      });
    }

    return history.map(mapApprovalHistoryItem);
  }, [approvalHistory, transactionID, isPendingApproval, matchingTier]);

  function mapApprovalHistoryItem(item: ApprovalHistoryItemType) {
    return [
      {
        label: t('approver', { defaultValue: 'Approver' }),
        value: item.Username,
      },
      {
        label: t('decision', { defaultValue: 'Decision' }),
        value: (
          <Box flx="col" gapSize="11px" ai="fe">
            <TransactionStatus
              status={item.ApprovalDecision.toLowerCase()}
              label={capitalizeFirstLetter(
                tGlobal(`Transaction.TransactionHistory.statuses.${item.ApprovalDecision.toLowerCase()}`, {
                  defaultValue: item.ApprovalDecision,
                }),
              )}
            />

            <div>{item.DecisionDate}</div>
          </Box>
        ),
      },
      {
        label: t('reason', { defaultValue: 'Reason' }),
        value: <ExpandableText text={item.Reason || ''} />,
        visible: item.Reason !== null,
      },
    ];
  }

  const tierDetails = matchingTier
    ? [
        {
          label: t('tier', { defaultValue: 'Tier' }),
          value: (
            <span className={cn('tier')}>
              {t('tier', { defaultValue: 'Tier' })} {matchingTier.tierNumber}
            </span>
          ),
        },
        {
          label: t('range', { defaultValue: 'Range' }),
          value: `${matchingTier.tierNumber > 1 ? '>' : ''}${currency} ${formattedCurrency(matchingTier.workflow.MinAmount)} → ${matchingTier.workflow.MaxAmount ? `${currency} ${formattedCurrency(matchingTier.workflow.MaxAmount)}` : t('noLimit', { defaultValue: 'No Limit' })}`,
        },
        {
          label: t('approvalsRequired', { defaultValue: 'Approvals Required' }),
          value: matchingTier.workflow.NumberOfApprovalsRequired,
        },
      ]
    : [];

  if (!isApprovalWorkflowEnabled) return null;
  if (!isPendingApproval && approvalHistory.length === 0) return null;

  return (
    <Card className={cn('detailRowsCard')}>
      <Title level="4" className={cn('title')}>
        {t('title', { defaultValue: 'Approval History' })}
      </Title>

      <div className={cn('tierDetails')}>
        <DetailsRows details={tierDetails} />
      </div>

      {approvalHistoryDetails.map((detail, index) => (
        <DetailsRows key={index} className={cn('row')} details={detail} />
      ))}
    </Card>
  );
}
